import { useTranslation } from 'react-i18next';
import { Layers } from 'lucide-react';
import { formatDateMedium } from '@/lib/format-date';

interface Fleet {
  _id: string;
  name: string;
  description?: string;
  tags?: string[];
  isDefault: boolean;
  deviceCount: number;
  onlineCount: number;
  staleCount: number;
  offlineCount: number;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  fleets: Fleet[];
  loading?: boolean;
}

/**
 * Fleet card grid for staff customer drill-in. Read-only in Phase 1
 * (no CRUD — spec §1.7). Duplicates the apps/web FleetCard shape per §6.4.
 */
export function StaffFleetGrid({ fleets, loading }: Props) {
  const { t } = useTranslation();

  if (loading && !fleets.length) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">{t('Loading fleets…')}</div>
    );
  }

  if (!fleets.length) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {t('No fleets yet. The customer has only the Default fleet.')}
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
      data-testid="staff-fleet-grid"
    >
      {fleets.map((fleet) => (
        <FleetCard key={fleet._id} fleet={fleet} />
      ))}
    </div>
  );
}

function FleetCard({ fleet }: { fleet: Fleet }) {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
      data-testid="staff-fleet-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-muted-foreground" />
          <span className="text-base font-semibold text-foreground">
            {fleet.isDefault ? t('Default') : fleet.name}
          </span>
        </div>
        {fleet.isDefault ? (
          <span className="rounded-sm bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {t('Default')}
          </span>
        ) : null}
      </div>

      {fleet.description ? (
        <p className="text-sm text-muted-foreground line-clamp-2">{fleet.description}</p>
      ) : null}

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-3">
          <StatusTile count={fleet.onlineCount} label={t('online')} tone="text-success" />
          <StatusTile count={fleet.staleCount} label={t('stale')} tone="text-warning" />
          <StatusTile count={fleet.offlineCount} label={t('offline')} tone="text-destructive" />
        </div>
        <span className="font-semibold text-foreground">
          {fleet.deviceCount} {t('devices')}
        </span>
      </div>

      {fleet.tags && fleet.tags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {fleet.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-sm bg-accent px-2 py-0.5 text-xs text-accent-foreground"
            >
              {tag}
            </span>
          ))}
        </div>
      ) : null}

      <div className="mt-auto text-xs text-muted-foreground">
        {t('Updated')} {formatDateMedium(fleet.updatedAt, '—')}
      </div>
    </div>
  );
}

function StatusTile({ count, label, tone }: { count: number; label: string; tone: string }) {
  return (
    <span className={`inline-flex items-center gap-1 ${tone}`}>
      <span className="font-semibold">{count}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </span>
  );
}
